import { API_BASE } from '../config';
import { clampEntityRelevance } from './entity-relevance';
import {
  mapMentionableEntitiesFromApiResponse,
  type MentionableEntitiesApiResponse,
} from './mentionable-entities';
import { stripHtml } from './note-utils';
import {
  buildReferencedInByTargetId,
  type EntityReferenceEdge,
  type RelatedEntityLookupItem,
} from './related-entities';
import type { RelatedEntity } from './entity-utils';

export type MentionHoverPreview =
  | {
      type: 'character';
      title: string;
      relevance?: number;
      role?: string;
      descriptionHtml?: string;
      referencedIn: RelatedEntity[];
    }
  | {
      type: 'location';
      title: string;
      relevance?: number;
      narrativeFunction?: string;
      descriptionHtml?: string;
    }
  | {
      type: 'manual_chapter';
      title: string;
      wordsGoal?: number;
      summary?: string;
    }
  | {
      type: 'storyline';
      title: string;
      descriptionHtml?: string;
    }
  | {
      type: 'scene';
      title: string;
      descriptionHtml?: string;
    }
  | {
      type: 'timeline_item';
      title: string;
      startDate?: string;
      startPrecision?: string;
      startTimezone?: string | null;
      endDate?: string;
      endPrecision?: string;
      endTimezone?: string | null;
      descriptionHtml?: string;
      itemType?: string;
      tags: string[];
      referencedIn: RelatedEntity[];
    }
  | {
      type: 'resource';
      title: string;
      tags: string[];
      createdAt?: string;
      resourceDate?: string | null;
    }
  | {
      type: 'document';
      title: string;
      createdAt?: string;
      updatedAt?: string;
    }
  | {
      type: 'user';
      title: string;
    }
  | {
      type: 'unknown';
      title: string;
    };

export type FetchMentionHoverPreviewParams = {
  entity: RelatedEntity;
  projectId: string;
  token: string | null;
  masterKey: string | null;
  signal?: AbortSignal;
};

type ProjectRelatedCache = {
  mentionable: RelatedEntityLookupItem[];
  refs: EntityReferenceEdge[];
};

type ListCacheEntry<T> = {
  items: T[];
};

const projectRelatedCache = new Map<string, Promise<ProjectRelatedCache>>();
const charactersCache = new Map<string, Promise<ListCacheEntry<CharacterApiRow>>>();
const locationsCache = new Map<string, Promise<ListCacheEntry<LocationApiRow>>>();
const chaptersCache = new Map<string, Promise<ListCacheEntry<ChapterApiRow>>>();
const storylinesCache = new Map<string, Promise<ListCacheEntry<StorylineApiRow>>>();
const scenesCache = new Map<string, Promise<ListCacheEntry<SceneApiRow>>>();
const timelineCache = new Map<string, Promise<ListCacheEntry<TimelineApiRow>>>();
const resourcesCache = new Map<string, Promise<ListCacheEntry<ResourceApiRow>>>();
const documentsCache = new Map<string, Promise<ListCacheEntry<DocumentApiRow>>>();

type CharacterApiRow = {
  id: string;
  name?: string;
  description?: string;
  role?: string;
  relevance?: number;
};

type LocationApiRow = {
  id: string;
  name?: string;
  description?: string | Record<string, unknown>;
  narrativeFunction?: string | Record<string, unknown>;
  relevance?: number;
};

type ChapterApiRow = {
  id: string;
  title?: string;
  wordsGoal?: number;
  summary?: string;
  isManual?: boolean;
};

type StorylineApiRow = {
  id: string;
  title?: string;
  description?: string;
};

type SceneApiRow = {
  id: string;
  title?: string;
  description?: string;
};

type TimelineApiRow = {
  id: string;
  name?: string;
  description?: string;
  item_type?: string;
  tags?: string[];
  start_date?: string;
  start_precision?: string;
  start_timezone?: string | null;
  end_date?: string | null;
  end_precision?: string | null;
  end_timezone?: string | null;
};

type ResourceApiRow = {
  id: string;
  title?: string;
  tags?: string[];
  createdAt?: string;
  resourceDate?: string | null;
};

type DocumentApiRow = {
  id: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
};

function authHeaders(token: string | null, masterKey: string | null): HeadersInit {
  if (token && !masterKey) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function withMasterKey(url: string, masterKey: string | null): string {
  if (!masterKey) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}masterKey=${encodeURIComponent(masterKey)}`;
}

export function hasRichTextContent(html: string | undefined | null): boolean {
  return Boolean(stripHtml(html ?? '').trim());
}

export function hasPlainTextContent(value: string | undefined | null): boolean {
  return Boolean(value?.trim());
}

export function shouldShowRelevance(value: number | undefined): value is number {
  return value != null && value > 0;
}

function optionalText(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function optionalHtml(html: string | undefined): string | undefined {
  return hasRichTextContent(html) ? html : undefined;
}

async function fetchJson<T>(
  url: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(withMasterKey(url, masterKey), {
    headers: authHeaders(token, masterKey),
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  return (await response.json()) as T;
}

function getOrCreateCache<T>(
  cache: Map<string, Promise<T>>,
  key: string,
  loader: () => Promise<T>
): Promise<T> {
  const existing = cache.get(key);
  if (existing) {
    return existing;
  }
  const promise = loader().catch((error) => {
    cache.delete(key);
    throw error;
  });
  cache.set(key, promise);
  return promise;
}

function stringifyMaybeStructuredField(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return optionalText(value);
  }
  if (typeof value === 'object') {
    const joined = Object.entries(value as Record<string, unknown>)
      .map(([key, entry]) => `${key}: ${typeof entry === 'string' ? entry : JSON.stringify(entry)}`)
      .join('. ');
    return optionalText(joined);
  }
  return optionalText(String(value));
}

async function loadProjectRelated(
  projectId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ProjectRelatedCache> {
  return getOrCreateCache(projectRelatedCache, projectId, async () => {
    const [mentionableRaw, refsRaw] = await Promise.all([
      fetchJson<MentionableEntitiesApiResponse>(
        `${API_BASE}/api/projects/${projectId}/mentionable-entities`,
        token,
        masterKey,
        signal
      ),
      fetchJson<{ references?: EntityReferenceEdge[] }>(
        `${API_BASE}/api/projects/${projectId}/entity-references`,
        token,
        masterKey,
        signal
      ),
    ]);
    const mentionable = mapMentionableEntitiesFromApiResponse(mentionableRaw).map(
      (entity): RelatedEntityLookupItem => ({
        id: entity.id,
        type: entity.type,
        label: entity.label,
        documentId: entity.documentId,
      })
    );
    const refs = Array.isArray(refsRaw.references) ? refsRaw.references : [];
    return { mentionable, refs };
  });
}

async function loadCharacters(
  documentId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<CharacterApiRow>> {
  return getOrCreateCache(charactersCache, documentId, async () => {
    const data = await fetchJson<CharacterApiRow[] | { characters?: CharacterApiRow[] }>(
      `${API_BASE}/api/documents/${documentId}/characters`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.characters) ? data.characters : [];
    return { items };
  });
}

async function loadLocations(
  documentId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<LocationApiRow>> {
  return getOrCreateCache(locationsCache, documentId, async () => {
    const data = await fetchJson<LocationApiRow[] | { locations?: LocationApiRow[] }>(
      `${API_BASE}/api/documents/${documentId}/locations`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.locations) ? data.locations : [];
    return { items };
  });
}

async function loadChapters(
  documentId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<ChapterApiRow>> {
  return getOrCreateCache(chaptersCache, documentId, async () => {
    const data = await fetchJson<{
      chapters?: ChapterApiRow[];
      chaptersByLevel?: Record<string, ChapterApiRow[]>;
    }>(`${API_BASE}/api/documents/${documentId}/chapters`, token, masterKey, signal);
    if (Array.isArray(data.chapters)) {
      return { items: data.chapters };
    }
    const byLevel = data.chaptersByLevel ?? {};
    const items = Object.values(byLevel).flat();
    return { items };
  });
}

async function loadStorylines(
  documentId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<StorylineApiRow>> {
  return getOrCreateCache(storylinesCache, documentId, async () => {
    const data = await fetchJson<StorylineApiRow[] | { storylines?: StorylineApiRow[] }>(
      `${API_BASE}/api/documents/${documentId}/storylines`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.storylines) ? data.storylines : [];
    return { items };
  });
}

async function loadScenes(
  documentId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<SceneApiRow>> {
  return getOrCreateCache(scenesCache, documentId, async () => {
    const data = await fetchJson<SceneApiRow[] | { scenes?: SceneApiRow[] }>(
      `${API_BASE}/api/documents/${documentId}/scenes`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.scenes) ? data.scenes : [];
    return { items };
  });
}

async function loadTimelineItems(
  projectId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<TimelineApiRow>> {
  return getOrCreateCache(timelineCache, projectId, async () => {
    const data = await fetchJson<TimelineApiRow[] | { items?: TimelineApiRow[] }>(
      `${API_BASE}/api/projects/${projectId}/timeline-items`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
    return { items };
  });
}

async function loadResources(
  projectId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<ResourceApiRow>> {
  return getOrCreateCache(resourcesCache, projectId, async () => {
    const data = await fetchJson<ResourceApiRow[] | { resources?: ResourceApiRow[] }>(
      `${API_BASE}/api/projects/${projectId}/resources`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.resources) ? data.resources : [];
    return { items };
  });
}

async function loadDocuments(
  projectId: string,
  token: string | null,
  masterKey: string | null,
  signal?: AbortSignal
): Promise<ListCacheEntry<DocumentApiRow>> {
  return getOrCreateCache(documentsCache, projectId, async () => {
    const data = await fetchJson<DocumentApiRow[] | { documents?: DocumentApiRow[] }>(
      `${API_BASE}/api/projects/${projectId}/documents`,
      token,
      masterKey,
      signal
    );
    const items = Array.isArray(data) ? data : Array.isArray(data.documents) ? data.documents : [];
    return { items };
  });
}

export function clearMentionHoverPreviewCaches(): void {
  projectRelatedCache.clear();
  charactersCache.clear();
  locationsCache.clear();
  chaptersCache.clear();
  storylinesCache.clear();
  scenesCache.clear();
  timelineCache.clear();
  resourcesCache.clear();
  documentsCache.clear();
}

export async function fetchMentionHoverPreview(
  params: FetchMentionHoverPreviewParams
): Promise<MentionHoverPreview> {
  const { entity, projectId, token, masterKey, signal } = params;
  const fallbackTitle = entity.label || entity.id;

  switch (entity.type) {
    case 'character': {
      if (!entity.documentId) {
        return { type: 'character', title: fallbackTitle, referencedIn: [] };
      }
      const [{ items }, related] = await Promise.all([
        loadCharacters(entity.documentId, token, masterKey, signal),
        loadProjectRelated(projectId, token, masterKey, signal),
      ]);
      const character = items.find((item) => item.id === entity.id);
      const relevance = clampEntityRelevance(character?.relevance);
      const referencedIn =
        buildReferencedInByTargetId(
          'character',
          entity.documentId,
          related.refs,
          related.mentionable
        ).get(entity.id) ?? [];
      return {
        type: 'character',
        title: optionalText(character?.name) ?? fallbackTitle,
        relevance: shouldShowRelevance(relevance) ? relevance : undefined,
        role: optionalText(character?.role),
        descriptionHtml: optionalHtml(character?.description),
        referencedIn,
      };
    }
    case 'location': {
      if (!entity.documentId) {
        return { type: 'location', title: fallbackTitle };
      }
      const { items } = await loadLocations(entity.documentId, token, masterKey, signal);
      const location = items.find((item) => item.id === entity.id);
      const relevance = clampEntityRelevance(location?.relevance);
      return {
        type: 'location',
        title: optionalText(location?.name) ?? fallbackTitle,
        relevance: shouldShowRelevance(relevance) ? relevance : undefined,
        narrativeFunction: stringifyMaybeStructuredField(location?.narrativeFunction),
        descriptionHtml:
          typeof location?.description === 'string'
            ? optionalHtml(location.description)
            : stringifyMaybeStructuredField(location?.description),
      };
    }
    case 'manual_chapter': {
      if (!entity.documentId) {
        return { type: 'manual_chapter', title: fallbackTitle };
      }
      const { items } = await loadChapters(entity.documentId, token, masterKey, signal);
      const chapter = items.find((item) => item.id === entity.id && item.isManual !== false);
      return {
        type: 'manual_chapter',
        title: optionalText(chapter?.title) ?? fallbackTitle,
        wordsGoal: typeof chapter?.wordsGoal === 'number' ? chapter.wordsGoal : undefined,
        summary: optionalText(chapter?.summary),
      };
    }
    case 'storyline': {
      if (!entity.documentId) {
        return { type: 'storyline', title: fallbackTitle };
      }
      const { items } = await loadStorylines(entity.documentId, token, masterKey, signal);
      const storyline = items.find((item) => item.id === entity.id);
      return {
        type: 'storyline',
        title: optionalText(storyline?.title) ?? fallbackTitle,
        descriptionHtml: optionalHtml(storyline?.description),
      };
    }
    case 'scene': {
      if (!entity.documentId) {
        return { type: 'scene', title: fallbackTitle };
      }
      const { items } = await loadScenes(entity.documentId, token, masterKey, signal);
      const scene = items.find((item) => item.id === entity.id);
      return {
        type: 'scene',
        title: optionalText(scene?.title) ?? fallbackTitle,
        descriptionHtml: optionalHtml(scene?.description),
      };
    }
    case 'timeline_item': {
      const [{ items }, related] = await Promise.all([
        loadTimelineItems(projectId, token, masterKey, signal),
        loadProjectRelated(projectId, token, masterKey, signal),
      ]);
      const item = items.find((row) => row.id === entity.id);
      const referencedIn =
        buildReferencedInByTargetId('timeline_item', null, related.refs, related.mentionable).get(
          entity.id
        ) ?? [];
      const tags = Array.isArray(item?.tags)
        ? item.tags.map((tag) => tag.trim()).filter(Boolean)
        : [];
      return {
        type: 'timeline_item',
        title: optionalText(item?.name) ?? fallbackTitle,
        startDate: optionalText(item?.start_date),
        startPrecision: optionalText(item?.start_precision),
        startTimezone: item?.start_timezone ?? null,
        endDate: optionalText(item?.end_date),
        endPrecision: optionalText(item?.end_precision),
        endTimezone: item?.end_timezone ?? null,
        descriptionHtml: optionalHtml(item?.description),
        itemType: optionalText(item?.item_type),
        tags,
        referencedIn,
      };
    }
    case 'resource': {
      const { items } = await loadResources(projectId, token, masterKey, signal);
      const resource = items.find((item) => item.id === entity.id);
      const tags = Array.isArray(resource?.tags)
        ? resource.tags.map((tag) => tag.trim()).filter(Boolean)
        : [];
      return {
        type: 'resource',
        title: optionalText(resource?.title) ?? fallbackTitle,
        tags,
        createdAt: optionalText(resource?.createdAt),
        resourceDate: optionalText(resource?.resourceDate) ?? null,
      };
    }
    case 'document': {
      const { items } = await loadDocuments(projectId, token, masterKey, signal);
      const document = items.find((item) => item.id === entity.id);
      return {
        type: 'document',
        title: optionalText(document?.title) ?? fallbackTitle,
        createdAt: optionalText(document?.createdAt),
        updatedAt: optionalText(document?.updatedAt),
      };
    }
    case 'user':
      return { type: 'user', title: fallbackTitle };
    default:
      return { type: 'unknown', title: fallbackTitle };
  }
}
